'use client'

import dynamic from 'next/dynamic'
import Link from 'next/link'
import { useState } from 'react'
import { useTheme, alpha } from '@mui/material/styles'
import { AppBar, Container, Toolbar, Grid, useMediaQuery, SwipeableDrawer, Box } from '@mui/material'
import { MoreVert as MoreVertIcon } from '@mui/icons-material'

// Types
import type { AppbarProps } from '@/types/components/organisms/appbar'

// Components
const LogoMolecule = dynamic(() => import('@/components/molecules/logo'))
const MenuItemsMolecule = dynamic(() => import('@/components/molecules/items/menu'))
const PinkButtonAtom = dynamic(() => import('@/components/atoms/buttons/contained/pink'))
const FlashIconAtom = dynamic(() => import('@/components/atoms/icons/flash'))

const AppbarOrganism = (props: AppbarProps) => {
	// Props
	const { lng, menus, setFontSize } = props

	// Variables
	const muiTheme = useTheme()
	const greaterThanMedium = useMediaQuery(muiTheme.breakpoints.up('md'))
	const [open, setOpen] = useState<boolean>(false)
	const colors: string[] = ['#ffa1a1', '#ff9191', '#ff8484']

	return (
		<AppBar
			variant="outlined"
			position="static"
			sx={{
				border: 'inherit',
				backdropFilter: 'blur(20px)',
				'&.MuiPaper-root': { bgcolor: alpha('#070f2b', 0.17) }
			}}
		>
			<Toolbar
				sx={{
					minHeight: 'auto',
					my: 4.5,
					px: 0
				}}
			>
				<Container maxWidth="lg">
					<Grid container justifyContent="space-between" alignItems="center" position="relative">
						<Grid
							size={{ xs: 6, md: 3.5, lg: 3 }}
							onMouseEnter={() => {
								if (setFontSize) setFontSize(100)
							}}
							onMouseLeave={() => {
								if (setFontSize) setFontSize(10)
							}}
						>
							<Link href={`/${lng}`}>
								<LogoMolecule lng={lng} />
							</Link>
						</Grid>

						{greaterThanMedium && (
							<Grid
								component="nav"
								size={{ md: 5, lg: 6 }}
								display="inline-flex"
								justifyContent="center"
								onMouseEnter={() => {
									if (setFontSize) setFontSize(38)
								}}
								onMouseLeave={() => {
									if (setFontSize) setFontSize(10)
								}}
								sx={{
									'& .MuiList-root': {
										display: 'inline-flex',
										p: 0,
										'& li': {
											display: 'inline-flex',
											position: 'relative',
											mr: 1,
											'&:last-child': { mr: 'inherit' },
											'&:hover': { bgcolor: 'inherit' },
											'& a': {
												display: 'inline-flex',
												alignItems: 'center',
												p: { md: 0.5, lg: 1 },
												color: '#70768e',
												transition: 'all .3s ease',
												'&:hover': {
													color: 'common.white',
													animation: 'shake 0.5s',
													animationIterationCount: 'infinite'
												},
												'& .MuiTypography-root': {
													fontSize: { md: 14, lg: 16 },
													fontWeight: 800
												}
											},
											'&.menu-active a': { color: 'common.white' }
										}
									}
								}}
							>
								<MenuItemsMolecule lng={lng} items={menus} />
							</Grid>
						)}

						<Grid
							size={{ xs: 6, md: 3.5, lg: 3 }}
							sx={{
								'@keyframes open': {
									from: { transform: `translate(-50%, calc(-50% - 250px + 56px - 50%))` },
									to: { transform: `translate(-50%, calc(-50% - 250px + 56px))` }
								},
								'@keyframes openInAppbar': {
									from: { transform: `translateY(-150%)` },
									to: { transform: `translateY(0)` }
								}
							}}
						>
							<Grid container direction="row-reverse">
								<Grid size="auto" display={{ xs: 'none', md: 'inline-flex' }}>
									<Link href="tel:+989374292056">
										<PinkButtonAtom title="form:button.order" startIcon={<FlashIconAtom />} />
									</Link>
								</Grid>

								<Grid size="auto" display={{ md: 'none' }}>
									<PinkButtonAtom title="form:button.menu" startIcon={<MoreVertIcon />} onClick={() => setOpen(!open)} />
								</Grid>
							</Grid>

							{!greaterThanMedium && (
								<SwipeableDrawer
									anchor="top"
									elevation={0}
									open={open}
									onClose={() => setOpen(false)}
									onOpen={() => setOpen(true)}
									sx={{
										'& .MuiPaper-root': {
											height: '100dvh',
											position: 'relative',
											bgcolor: 'transparent',
											overflow: 'hidden'
										},
										'& .MuiList-root': {
											display: 'flex',
											flexDirection: 'column',
											justifyContent: 'center',
											alignItems: 'center',
											p: 0,
											bgcolor: 'transparent',
											'& li': {
												display: 'inline-flex',
												width: '50%',
												maxWidth: 200,
												minHeight: 'inherit',
												p: 0,
												'& a': {
													display: 'inline-flex',
													width: 1,
													p: 1,
													color: 'common.white',
													'& .MuiTypography-root': { textAlign: 'center' },
													'&:hover': {
														color: 'common.white',
														animation: 'shake 0.5s',
														animationIterationCount: 'infinite'
													}
												}
											}
										}
									}}
								>
									<Toolbar />

									{open && (
										<Box
											component="nav"
											sx={{
												transform: `translateY(-150%)`,
												animation: `openInAppbar 1s ease 1s forwards`
											}}
										>
											<MenuItemsMolecule lng={lng} items={menus} />
										</Box>
									)}

									{open && (
										<Box position="absolute" top={0} left="50%" zIndex={-1} sx={{ transform: 'translateX(-50%)' }}>
											{Array.from(Array(3)).map((_, index) => (
												<Box
													key={index}
													width={1000 - index * 100}
													height={1000 - index * 100}
													position="absolute"
													top="50%"
													left="50%"
													bgcolor={colors[index]}
													borderRadius="50%"
													sx={{
														transform: `translate(-50%, calc(-50% - 250px + 56px - 50%))`,
														animation: `open 1s ease ${index * 0.25}s forwards`
													}}
												/>
											))}
										</Box>
									)}
								</SwipeableDrawer>
							)}
						</Grid>
					</Grid>
				</Container>
			</Toolbar>
		</AppBar>
	)
}

export default AppbarOrganism
